<template src="./qa.html"></template>

<script lang="ts">
import { nextTick } from 'vue'
import { callTurnToConfigFn } from 'util/turnto-utils'
import CommentsQuestion from 'components/question/CommentsQuestion.vue'
import { ConfirmedSubmitInfo, RegistrationInfo, SubmissionContext }
  from 'components/answers/answer-reply-submission-context'
import { DoMoreProps } from 'util/interfaces'
import domUtils from 'util/dom-utils'
import globalConfig from 'init/global.config'
import globalStore, { Store } from 'init/global-store'
import Menu from 'objects/menu/Menu.vue'
import { localizedThousandsInteger } from 'util/localization-utils'
import qaConfig, { RelatedQuestionsDisplayTypes } from './qa.config'
import logger from 'util/logger'
import MenuList from 'objects/menu/MenuList.vue'
import PaginateMixin from 'mixins/PaginateMixin.vue'
import Question from 'components/question/Question.vue'
import RelatedQuestionsList from 'components/related-questions-list/RelatedQuestionsList.vue'
import Pagination from 'objects/pagination/Pagination.vue'
import questionsListService from 'services/api/questions-list'
import { RestError, SortOption } from 'services/api-service'
import submissionStore from 'store/submission-store'
import submissionLoader from 'widgets/submission/submission-loader'
import trackingService from 'services/tracking-service'
import UserToolbar from 'components/user-toolbar/UserToolbar.vue'
import { register as registerObserver, unregister as unregisterObserver }
  from 'util/visible-observer'

const MIN_QUESTIONS_FOR_SORT = 3

export default {
  name: 'QA',
  data() {
    return {
      forType: '', // for registration form
      globalStoreState: globalStore.state,
      hasOneOrMoreComments: false,
      previousSku: '',
      questions: [],
      sortBy: '',
      showFullQuestion: !qaConfig.enableLazyLoading,
      showMoreDisabled: false
    }
  },
  provide() {
    return {
      submissionCtx: this.getSubmissionContext()
    }
  },
  components: {
    'tt-comments-question': CommentsQuestion,
    'tt-menu': Menu,
    'tt-menu-list': MenuList,
    'tt-pagination': Pagination,
    'tt-question': Question,
    'tt-user-toolbar': UserToolbar,
    'tt-related-questions-list': RelatedQuestionsList
  },
  mixins: [PaginateMixin],
  computed: {
    currentSku(): string {
      return (this.globalStoreState as Store).sku
    },
    formattedTotal() {
      return localizedThousandsInteger(this.totalWithComments)
    },
    qaConfig() {
      return qaConfig
    },
    showSort() {
      return this.questions.length >= MIN_QUESTIONS_FOR_SORT
    },
    sortByTitle() {
      return this.$msg(`qa.sortBy.${this.sortBy}`)
    },
    sortOptions() {
      return qaConfig.sortOptions.map((option) => {
        return { name: option, title: this.$msg(`qa.sortBy.${option}`) } as SortOption
      })
    },
    totalWithComments() {
      return (this.hasOneOrMoreComments ? 1 : 0) + this.total
    },
    showRelatedList() {
      return qaConfig.relatedQuestionsDisplay === RelatedQuestionsDisplayTypes.SEPARATE
    },
    useShowMore() {
      return qaConfig.useShowMore
    }
  },
  methods: {
    fetchData(isPageChange) {
      if (this.currentSku && (this.offset === 0 || this.offset <= this.total)) {
        const limit = this.offset === 0 ? this.initialPageSize : this.pageSize
        if (qaConfig.useShowMore && this.offset !== 0) {
          this.showMoreDisabled = true
        }
        const isFirstCall = this.currentSku !== this.previousSku
        if (isFirstCall) {
          trackingService.queueEvent('qa', 'load', this.currentSku)
        }
        questionsListService.questionsList({
          sku: this.currentSku,
          offset: this.offset,
          limit,
          sort: this.sortBy,
          includeRelated: qaConfig.relatedQuestionsDisplay === RelatedQuestionsDisplayTypes.INCLUDED
        }).then((data) => {
          this.previousSku = this.currentSku
          globalStore.catItemId = data.catalogItemId
          globalStore.parentCategoryIds = data.parentCategoryIds
          globalStore.brand = data.brand
          if (qaConfig.useShowMore && this.offset !== 0) {
            this.questions.push(...data.questions)
            this.showMoreDisabled = false
          } else {
            this.questions = data.questions
          }
          // this.total in paginate-mixin
          this.total = data.total
          if (isPageChange) {
            // not first time, so may have paginated.  Will only scroll up, not down
            nextTick(() => {
              // TT-11837 - focus header after scroll. safeFocus in case client hides the header
              const focusFn = () => domUtils.safeFocus(() => this.$refs.total)
              domUtils
                .scrollUpToRef(this.$refs, 'qList', globalConfig.additionalPagingScroll)
                .then(focusFn)
                .catch(focusFn)
            })
          }
          if (isFirstCall) {
            nextTick(() => { callTurnToConfigFn('qa', 'onFinish') })
          }
        }).catch((e: RestError) => {
          if (e.errorCode === 'turnto-offline') {
            logger.info('TurnTo is in offline mode - Q&A widget not loaded', e)
          } else {
            logger.warn('Error loading data for the Q&A widget', e)
          }
        })
      }
    },
    getSubmissionContext() {
      const submissionCtx = new SubmissionContext()
      submissionCtx.eventBus.on('confirmedSubmitComplete', (info: ConfirmedSubmitInfo) => {
        submissionStore.startFlowWithCatItem(
          info.catItem,
          info.siteKey,
          {
            page: 'do-more',
            props: { msgType: info.ugcType, questionId: info.questionId } as DoMoreProps
          }
        )
        submissionLoader.load()
      })
      submissionCtx.eventBus.on('showRegistration', this.openRegistration)
      submissionCtx.eventBus.on('showError', this.onShowError)
      return submissionCtx
    },
    onShowError(providedMessage) {
      const message = providedMessage || this.$msg('flowError.2000')
      import(/* webpackChunkName: 'standalone-popover' */ 'widgets/standalone-popover/standalone-popover-loader').then((popoverLoader) => {
        popoverLoader.default.load({ message, type: 'error', autoClose: true })
      })
    },
    openRegistration(registrationInfo: RegistrationInfo) {
      // eslint-disable-next-line max-len
      import(/* webpackChunkName: 'registration-modal' */ 'components/registration/registration-modal-loader').then((registrationModalLoader) => {
        registrationModalLoader.default.load({
          stagedContentToken: registrationInfo.stagedContentToken,
          registerFor: registrationInfo.registerFor
        })
      })
    },
    sortOrderSelected(sortOption: SortOption) {
      // sortBy change is watched in PaginateMixin to execute the query
      this.sortBy = sortOption.name
    },
    commentsLoaded(hasOneOrMoreComments) {
      this.hasOneOrMoreComments = hasOneOrMoreComments
    }
  },
  watch: {
    // whenever the currentSku changes, we need to reload the questions
    currentSku() {
      this.sortBy = qaConfig.defaultSortOption
      // method in PaginateMixin
      this.onQueryChange()
    }
  },
  created() {
    // initialPageSize and pageSize are in PaginateMixin
    this.initialPageSize = qaConfig.initialPageSize || qaConfig.pageSize
    this.pageSize = qaConfig.pageSize
    this.sortBy = qaConfig.defaultSortOption
    // method in PaginateMixin
    this.onQueryChange()
  },
  mounted() {
    if (qaConfig.enableLazyLoading) {
      const rootElem = this.$el
      registerObserver(rootElem, () => {
        this.showFullQuestion = true
        unregisterObserver(rootElem)
      })
    }
  }
}
</script>

<style lang="scss" src="./qa.scss"></style>
